/**
 * Tago Design Tokens — Single source of truth for all visual constants.
 *
 * Every component uses these tokens; no raw values in component files.
 * Re-exports brand primitives for backward compatibility, then extends
 * with semantic layers for z-index, shadow, duration, haptic, typography,
 * and light/dark color resolution.
 *
 * @see docs/milestones/M0.6-UI-PRIMITIVES.md §2
 */

// Re-export the brand primitives that screens pull in alongside the semantic
// layers below. `COLORS` and `GRADIENTS` are imported straight from
// `lib/brand` at every call site, so they are not re-exported here.
export { FONTS, SPACING, RADIUS } from '../brand'

import { SPACING as _SPACING } from '../brand'

// ─── Form-field rhythm ──────────────────────────────────────────────────────

/**
 * Vertical gap between rows of form fields. Owned by the input components
 * themselves (TextInput, PhoneInput, DatePicker, Select) so screens never
 * have to think about row spacing — every form field reserves the same gap
 * below itself. Bump this in one place to re-rhythm every form in the app.
 *
 * Was previously duplicated across components as `mb-7` (NativeWind, 28px)
 * vs `SPACING.xl - SPACING.xs` (math, 28px). Result was visually
 * inconsistent because the NativeWind path occasionally drops on
 * Animated.View + Reanimated style combos. One token, one source of truth.
 */
export const FORM_FIELD_GAP = _SPACING.xl // 32px

// ─── Z-Index ───────────────────────────────────────────────────────────────

export const Z_INDEX = {
  base: 0,
  raised: 1,
  dropdown: 100,
  sticky: 200,
  fixed: 300,
  overlay: 400,
  modal: 500,
  popover: 600,
  toast: 700,
} as const

// ─── Shadow / Elevation ────────────────────────────────────────────────────

/**
 * Platform-aware shadow presets.
 * iOS: shadow* props  |  Android: elevation  |  Web: boxShadow via StyleSheet
 */
export const SHADOWS = {
  none: {
    shadowColor: 'transparent',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0,
    shadowRadius: 0,
    elevation: 0,
  },
  sm: {
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.08,
    shadowRadius: 3,
    elevation: 2,
  },
  md: {
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.12,
    shadowRadius: 6,
    elevation: 4,
  },
  lg: {
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.16,
    shadowRadius: 12,
    elevation: 8,
  },
  xl: {
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.2,
    shadowRadius: 24,
    elevation: 16,
  },
} as const

// ─── Animation Durations ───────────────────────────────────────────────────

/**
 * The complete motion vocabulary. Five values, no sixth.
 *
 * Before this token set had consumers, the app shipped 21 distinct hardcoded
 * durations and 31 distinct hardcoded delays across 503 call sites — 400ms
 * next to 450ms next to 380ms, none of it decided, all of it hand-typed. A
 * design system with twenty durations has none: nothing reads as deliberate
 * because nothing repeats.
 *
 * How to choose:
 * - `instant`  press feedback, focus rings — anything the finger is still on.
 * - `fast`     state flips the user asked for (toggle, checkbox, tab).
 * - `normal`   entrances and exits. The default; reach for this first.
 * - `slow`     full-screen transitions and overlay dismissals.
 * - `verySlow` ambient loops (shimmer, pulse) that nobody waits on.
 *
 * @see docs/STANDARDS.md §8
 */
export const DURATION = {
  instant: 100,
  fast: 200,
  normal: 300,
  slow: 500,
  verySlow: 800,
} as const

/**
 * Entrance stagger unit, in milliseconds.
 *
 * A cascade is written as its ordinal position — `STAGGER * 1`, `STAGGER * 2`,
 * `STAGGER * 3` — never as hand-tuned literals. This is why the old code had
 * sequences like 50/120/160/180/220/260: each number was picked in isolation,
 * so no two screens cascaded at the same rhythm.
 *
 * Keep cascades short. Past ~7 steps the last element reads as broken rather
 * than choreographed, which is why {@link MAX_STAGGER_STEPS} exists.
 */
export const STAGGER = 50

/**
 * Cap for staggered sequences driven by a list index. Beyond this the delay
 * stops growing, so row 40 does not enter two seconds after row 1.
 */
export const MAX_STAGGER_STEPS = 5

// ─── Interaction Contracts ─────────────────────────────────────────────────

/**
 * Product-feel contracts that every tappable primitive should honor.
 *
 * These are intentionally named by UX outcome, not component implementation:
 * - 44px is the minimum comfortable target from the iOS HIG and Android
 *   touch-target guidance.
 * - pressLockMs prevents accidental double submits without making the first
 *   tap feel delayed.
 * - maxAsyncPressLockMs is the circuit breaker for tap actions that depend on
 *   network I/O; controls must never stay locked until the next app reload.
 * - hitSlop is the default invisible comfort padding for icon-only controls.
 */
export const INTERACTION = {
  minTouchTarget: 44,
  /**
   * Height of one form field row (TextInput, Select, DatePicker, TimeSlotPicker).
   *
   * Shared with the loading skeletons that stand in for those fields: the
   * skeleton guessed 50 while the real control was 54, and every field below
   * the first shifted when the form arrived.
   */
  fieldHeight: 54,
  /**
   * Gap between a field's leading icon and its text.
   *
   * This exists because it drifted. TextInput used `SPACING.sm + SPACING.xs`
   * (12) and DatePicker used `SPACING.sm + 2` (10) — two expressions that look
   * equally deliberate and are not equal. The icons still lined up, so nothing
   * looked broken in isolation; what broke was the column the text hangs from,
   * by 2px, on one row of a stack of six. That is invisible to review and
   * visible to the eye, which is the worst combination.
   *
   * Anything that puts an icon inside a field row reads this. Guarded by
   * scripts/validate-field-chrome.mjs.
   */
  fieldIconGap: 12,
  pressLockMs: 450,
  maxAsyncPressLockMs: 8000,
  iconHitSlop: 8,
} as const

// ─── Typography Scale ───────────────────────────────────────────────────────

/**
 * Named typography presets for consistent text styling.
 * All sizes in px (React Native logical pixels).
 * fontSize/lineHeight in logical px; letterSpacing in px;
 * fontWeight as string literal (React Native requirement).
 */
export const TEXT_STYLES = {
  /**
   * Hero / brand moment — Heavitas, the brand display face.
   *
   * Used sparingly for the highest-stakes typography in the app: splash
   * tagline, onboarding slide titles, welcome headline, celebration
   * overlay messages, booking-confirmation hero. Heavitas is a chunky
   * geometric display cut, so we drop the negative tracking that suits
   * Montserrat-Bold and let the natural letterforms breathe. Per
   * CLAUDE.md "FONTS.heading (Heavitas) — brand/hero text only".
   */
  display: {
    fontFamily: 'Heavitas' as const,
    fontSize: 36,
    lineHeight: 42,
    letterSpacing: 0,
  },
  /** Display, but in the body face — falls back gracefully if Heavitas fails to load. */
  displayBody: {
    fontFamily: 'Montserrat-Bold' as const,
    fontSize: 40,
    lineHeight: 48,
    letterSpacing: -0.5,
  },
  /** Screen title — 32px Bold */
  h1: { fontFamily: 'Montserrat-Bold' as const, fontSize: 32, lineHeight: 40, letterSpacing: -0.3 },
  /** Section header — 24px Bold */
  h2: { fontFamily: 'Montserrat-Bold' as const, fontSize: 24, lineHeight: 32, letterSpacing: -0.2 },
  /** Card title — 20px Bold */
  h3: { fontFamily: 'Montserrat-Bold' as const, fontSize: 20, lineHeight: 28, letterSpacing: 0 },
  /** Subheading — 18px Bold */
  h4: { fontFamily: 'Montserrat-Bold' as const, fontSize: 18, lineHeight: 26, letterSpacing: 0 },
  /** Large body — 18px SemiBold */
  bodyLg: {
    fontFamily: 'Montserrat-SemiBold' as const,
    fontSize: 18,
    lineHeight: 28,
    letterSpacing: 0,
  },
  /** Default body — 16px SemiBold */
  body: {
    fontFamily: 'Montserrat-SemiBold' as const,
    fontSize: 16,
    lineHeight: 24,
    letterSpacing: 0,
  },
  /** Secondary body — 14px SemiBold */
  bodySm: {
    fontFamily: 'Montserrat-SemiBold' as const,
    fontSize: 14,
    lineHeight: 22,
    letterSpacing: 0,
  },
  /** Form labels, tags — 14px Bold */
  label: {
    fontFamily: 'Montserrat-Bold' as const,
    fontSize: 14,
    lineHeight: 20,
    letterSpacing: 0.2,
  },
  /** Timestamps, hints — 12px SemiBold */
  caption: {
    fontFamily: 'Montserrat-SemiBold' as const,
    fontSize: 12,
    lineHeight: 18,
    letterSpacing: 0.1,
  },
  /** Code, debug — 14px mono */
  mono: { fontFamily: 'SpaceMono' as const, fontSize: 14, lineHeight: 20, letterSpacing: 0 },
} as const

// ─── Light & Dark Semantic Color Mapping ───────────────────────────────────

/**
 * Semantic color tokens that resolve differently in light vs dark mode.
 * Use these in components via `useColorScheme()` instead of raw `COLORS`.
 *
 * @param isDark - Whether the current color scheme is dark.
 * @returns Fully resolved semantic color palette.
 *
 * @example
 * ```ts
 * const colors = useSemanticColors(isDark)
 * <View style={{ backgroundColor: colors.surface }} />
 * ```
 */
export function useSemanticColors(isDark: boolean) {
  return {
    // Surfaces
    background: isDark ? '#110E1C' : '#F5F5F7',
    surface: isDark ? '#242140' : '#FFFFFF',
    // A raised surface is `surface` lifted one step, not a brand statement.
    // In dark this used to be the full brand purple (#480C82), so everything
    // painted with it — the Wompi note on /profile/payment-methods, the header
    // "+" disc, the Select/DatePicker sheets, the map overlay chips — turned
    // into a saturated purple slab that outshouted the actual CTA next to it,
    // while the same token in light was plain #FFFFFF, byte-identical to
    // `surface`. Two themes, two different objects. #2C2947 keeps the purple
    // cast of the dark palette and sits exactly one step above `surface`
    // (relative luminance 0.026 vs 0.018), so a raised element reads as
    // lifted in both themes and every glyph on it gains contrast rather than
    // losing it.
    surfaceRaised: isDark ? '#2C2947' : '#FFFFFF',
    surfaceSubtle: isDark ? 'rgba(255,255,255,0.04)' : '#F0EFF4',
    // The line that says "this is where a thing ends". In dark it does that
    // job: composited over a surface it lands at #35323E on the app
    // background — 1.55:1, a hairline you can actually see. In light it was
    // #E5E5EA, which against the page (#F5F5F7) is 1.09:1 and against the
    // field fill (#F0EFF4) 1.10:1 — under 1.1 either way, i.e. not a boundary
    // at all. The email field on /forgot-password had no perceptible edge:
    // the only thing marking a 54pt tap target was its placeholder.
    //
    // #C9C9D2 is the light value that reproduces the dark theme's own
    // relationship rather than a number picked by eye: 1.51:1 on the page,
    // 1.65:1 on a white card, 1.44:1 on a filled field — dark measures
    // 1.80 / 1.46 / 1.43 for the same three. Same hue family as before, one
    // step down the ramp, so every divider, card outline and control edge in
    // light gets the weight its dark counterpart already had.
    border: isDark ? 'rgba(255,255,255,0.12)' : '#C9C9D2',
    borderFocused: isDark ? '#F0422D' : '#480C82',

    // Text — 4-tier hierarchy: primary > secondary > muted > disabled
    // Light: near-black → medium gray → light gray → faded
    // Dark: white → 65% → 52% → 25%
    //
    // `textMuted` used to be #7B7B90 / 40% white and failed WCAG AA for normal
    // text on every surface it is painted on. Measured:
    //
    //            light on #F5F5F7   light on #FFFFFF   dark on #110E1C   dark on #242140
    //   before        3.80               4.14              3.79              3.06
    //   now           4.50               4.90              5.30              4.75
    //
    // It is used 185 times, including the bottom tab-bar labels, so this was
    // not decorative grey — it is text people are expected to read.
    //
    // The values are the LIGHTEST that still clear 4.5:1, deliberately. The
    // four-tier scale asks for a step between `textSecondary` (4.77 on the app
    // background) and legibility, and there is barely room for one: muted now
    // sits a hair below secondary rather than visibly below it. That
    // compression is the honest trade — a tier that cannot be read is not a
    // tier, and `textDisabled` is left alone because disabled controls are
    // exempt from the contrast minimum.
    // A later pass found the ramp had been solved against two surfaces when
    // there are three: `surfaceSubtle` (#F0EFF4) is the background of every
    // TextInput, Select and DatePicker, and it is darker than the app
    // background. The Select placeholder — "Seleccioná tu banco" on the payout
    // forms — measured 4.29:1 there.
    //
    // Darkening muted alone would have made it darker than secondary and
    // inverted the hierarchy, so both moved. Worst-surface readings now:
    // primary 15.59, secondary 5.70, muted 4.76.
    textPrimary: isDark ? '#FFFFFF' : '#1A1525',
    textSecondary: isDark ? 'rgba(255,255,255,0.65)' : '#5C5C70',
    textMuted: isDark ? 'rgba(255,255,255,0.52)' : '#68687C',
    textDisabled: isDark ? 'rgba(255,255,255,0.25)' : '#B0B0BA',
    textInverse: isDark ? '#1A1525' : '#FFFFFF',

    // Brand color used as TEXT. The brand ramp is built for fills, and a fill
    // color and a text color are not the same job: the raw brand values fail
    // AA in one theme each, in opposite directions.
    //
    //                      light #FFFFFF   light #F5F5F7   dark #242140   dark #110E1C
    //   coral.DEFAULT          3.80            3.49           4.04           5.00
    //   coral.light            2.82            2.59           5.44           6.74
    //   coral.dark             5.50            5.05           2.80           3.46
    //   purple.DEFAULT        12.72           11.69           1.21           1.50
    //   purple.light           8.44            7.75           1.82           2.26
    //
    // Purple is the sharper failure: as text on a dark surface the entire ramp
    // sits between 1.10 and 2.26 — the "Desbloquear" action on the blocked-users
    // list was 1.21:1, which is not low contrast, it is invisible. No existing
    // ramp value fixes it, so `textBrand` in dark mode is a derived tint that
    // holds the brand hue (272°) at a lightness that clears AA: 6.32:1 on the
    // worst dark surface.
    //
    // Use these anywhere a brand color IS the glyph. `COLORS.coral.*` stays
    // correct for fills, borders, icons and decoration, where 3:1 governs.
    textBrand: isDark ? '#BF95E4' : '#480C82',
    textAccent: isDark ? '#FF6A52' : '#C72D1D',
    // Error in its TEXT role. It cannot share `error` above: a fill needs to be
    // dark enough for white to sit on it, and a glyph on a dark surface needs
    // to be light — opposite requirements from one token. Split, both pass:
    // 5.05 in light, 5.44 in dark, against the worst surface of each.
    textError: isDark ? '#FF6A52' : '#C72D1D',

    // Interactive
    primary: '#F0422D', // coral — same in both modes
    primaryDark: '#C72D1D',
    primaryLight: '#FF6A52',

    // Status
    // Status colors, in their FILL role: a solid swatch that white sits on, an
    // icon, or an 8%-alpha tint. Light mode was tuned for the tint and failed
    // as text — success 3.46:1 and warning 2.93:1 on the app background — so
    // both are darkened here. That is safe in every direction: darker on a
    // light surface only helps icons and tints, and the three solid fills all
    // carry white.
    success: isDark ? '#34D399' : '#047857',
    warning: isDark ? '#FBBF24' : '#A84E08',
    // Coral in both modes, but the dark end of the ramp, because this one IS a
    // fill: ConfirmDialog paints the destructive button with it and puts white
    // on top, which measured 3.80:1 at the old value.
    error: '#C72D1D',
    info: isDark ? '#60A5FA' : '#2563EB',

    // Overlays
    scrim: 'rgba(0,0,0,0.55)',
    shimmerBase: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
    shimmerHighlight: isDark ? 'rgba(255,255,255,0.10)' : 'rgba(249,168,38,0.06)',
  } as const
}

// ─── Color math ────────────────────────────────────────────────────────────

/**
 * Apply an alpha channel to any token color.
 *
 * Semantic tokens are hex in some slots (`surface`) and `rgba()` in others
 * (`border` in dark mode), so the two hand-rolled idioms that grew around
 * them — `` `${COLORS.coral.DEFAULT}66` `` and a private copy of this
 * function inside `Chip.tsx` — each only worked on half the palette. Appending
 * a hex pair to `rgba(255,255,255,0.12)` produces a string no renderer
 * accepts, and it fails silently: the view just draws with no background.
 *
 * Use this instead of concatenating. `alpha` is 0–1, matching every other
 * opacity in the codebase.
 */
export function withAlpha(color: string, alpha: number): string {
  const clamped = Math.min(1, Math.max(0, alpha))
  const rgbaMatch = color.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/)
  if (rgbaMatch) {
    return `rgba(${rgbaMatch[1]}, ${rgbaMatch[2]}, ${rgbaMatch[3]}, ${clamped})`
  }
  let hex = color.replace('#', '')
  if (hex.length === 3) {
    hex = hex
      .split('')
      .map((c) => c + c)
      .join('')
  }
  hex = hex.slice(0, 6)
  const r = Number.parseInt(hex.slice(0, 2), 16)
  const g = Number.parseInt(hex.slice(2, 4), 16)
  const b = Number.parseInt(hex.slice(4, 6), 16)
  if (![r, g, b].every((channel) => Number.isFinite(channel))) {
    return `rgba(0, 0, 0, ${clamped})`
  }
  // rgba(), never #RRGGBBAA. Android's native color parser and Reanimated's
  // interpolateColor treat an 8-digit hex as #AARRGGBB, so a CSS-style
  // suffix alpha either paints the wrong color or SIGSEGVs the UI thread.
  return `rgba(${r}, ${g}, ${b}, ${clamped})`
}

/**
 * Same color, always `rgba(...)`. Use this before handing a token to a
 * Reanimated worklet — mixing `#C9C9D2` with `rgba(...)` in interpolateColor
 * is the Android crash that closed the Qualitas disclaimer screen.
 */
export function asRgba(color: string): string {
  if (/^rgba?\(/i.test(color)) return color
  return withAlpha(color, 1)
}
